import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface EventHubCatalogListItem {
  id: string;
  eventType: string;
  version: number;
  ownerModuleKey: string;
  status: 'Active' | 'Deprecated' | 'Retired' | number;
  description: string;
  deprecatedAtUtc?: string | null;
  retiredAtUtc?: string | null;
}

export interface EventHubCatalogConsumer {
  subscriptionId: string;
  tenantId: string;
  handlerKey: string;
  eventVersion?: number | null;
  isActive: boolean;
}

export interface EventHubCatalogVersionSummary {
  version: number;
  status: 'Active' | 'Deprecated' | 'Retired' | number;
  deprecatedAtUtc?: string | null;
  retiredAtUtc?: string | null;
}

export interface EventHubCatalogDetail {
  id: string;
  eventType: string;
  version: number;
  ownerModuleKey: string;
  status: 'Active' | 'Deprecated' | 'Retired' | number;
  description: string;
  schemaJson: string;
  exampleJson: string;
  deprecatedAtUtc?: string | null;
  retiredAtUtc?: string | null;
  consumers: EventHubCatalogConsumer[];
  otherVersions: EventHubCatalogVersionSummary[];
}

@Injectable({ providedIn: 'root' })
export class EventHubApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/api/v1/event-hub`;

  listCatalog(filters?: {
    eventTypePrefix?: string;
    ownerModuleKey?: string;
    status?: string;
  }): Observable<EventHubCatalogListItem[]> {
    let params = new HttpParams();
    if (filters?.eventTypePrefix) {
      params = params.set('eventTypePrefix', filters.eventTypePrefix);
    }
    if (filters?.ownerModuleKey) {
      params = params.set('ownerModuleKey', filters.ownerModuleKey);
    }
    if (filters?.status) {
      params = params.set('status', filters.status);
    }
    return this.http.get<EventHubCatalogListItem[]>(`${this.baseUrl}/catalog`, { params });
  }

  getCatalogEntry(eventType: string, version: number): Observable<EventHubCatalogDetail> {
    return this.http.get<EventHubCatalogDetail>(`${this.baseUrl}/catalog/${encodeURIComponent(eventType)}/versions/${version}`);
  }

  listWebhookSubscriptions(): Observable<EventHubWebhookSubscriptionListItem[]> {
    return this.http.get<EventHubWebhookSubscriptionListItem[]>(`${this.baseUrl}/webhook-subscriptions`);
  }

  getWebhookSubscription(id: string): Observable<EventHubWebhookSubscriptionDetail> {
    return this.http.get<EventHubWebhookSubscriptionDetail>(`${this.baseUrl}/webhook-subscriptions/${id}`);
  }

  createWebhookSubscription(request: CreateEventHubWebhookSubscriptionRequest): Observable<EventHubWebhookSubscriptionDetail> {
    return this.http.post<EventHubWebhookSubscriptionDetail>(`${this.baseUrl}/webhook-subscriptions`, request);
  }

  updateWebhookSubscription(id: string, request: UpdateEventHubWebhookSubscriptionRequest): Observable<EventHubWebhookSubscriptionDetail> {
    return this.http.put<EventHubWebhookSubscriptionDetail>(`${this.baseUrl}/webhook-subscriptions/${id}`, request);
  }

  rotateWebhookSecret(id: string): Observable<RotateWebhookSecretResult> {
    return this.http.post<RotateWebhookSecretResult>(`${this.baseUrl}/webhook-subscriptions/${id}/rotate-secret`, {});
  }

  listWebhookDeliveries(subscriptionId?: string): Observable<EventHubWebhookDeliveryListItem[]> {
    let params = new HttpParams();
    if (subscriptionId) {
      params = params.set('subscriptionId', subscriptionId);
    }
    return this.http.get<EventHubWebhookDeliveryListItem[]>(`${this.baseUrl}/webhook-deliveries`, { params });
  }

  getObservabilityMetrics(): Observable<EventHubMetricsSnapshot> {
    return this.http.get<EventHubMetricsSnapshot>(`${this.baseUrl}/observability/metrics`);
  }

  getObservabilityAlerts(): Observable<EventHubAlertItem[]> {
    return this.http.get<EventHubAlertItem[]>(`${this.baseUrl}/observability/alerts`);
  }
}

export interface EventHubMetricsSnapshot {
  outboxLagSeconds: number;
  dlqDepth: number;
  dispatchThroughputPerMinute: number;
  webhookFailureRate: number;
  handlerLatencyP95Ms: number;
  collectedAtUtc: string;
}

export interface EventHubAlertItem {
  code: string;
  severity: string;
  message: string;
  raisedAtUtc: string;
  isActive: boolean;
}

export interface EventHubWebhookSubscriptionListItem {
  id: string;
  name: string;
  eventType: string;
  eventVersion?: number | null;
  endpointUrl: string;
  signatureKeyId: string;
  isActive: boolean;
  maxAttempts: number;
  maxDeliveriesPerMinute: number;
  deliverPii: boolean;
}

export interface EventHubWebhookSubscriptionDetail extends EventHubWebhookSubscriptionListItem {
  previousSignatureKeyId?: string | null;
  previousSigningSecretValidUntilUtc?: string | null;
  allowedDomains: string[];
  signingSecretMasked: string;
}

export interface CreateEventHubWebhookSubscriptionRequest {
  name: string;
  eventType: string;
  eventVersion?: number | null;
  endpointUrl: string;
  allowedDomains: string[];
  maxDeliveriesPerMinute: number;
  deliverPii: boolean;
  isActive: boolean;
  maxAttempts: number;
}

export interface UpdateEventHubWebhookSubscriptionRequest {
  name: string;
  endpointUrl: string;
  allowedDomains: string[];
  maxDeliveriesPerMinute: number;
  deliverPii: boolean;
  isActive: boolean;
  maxAttempts: number;
}

export interface RotateWebhookSecretResult {
  subscriptionId: string;
  newSignatureKeyId: string;
  previousSignatureKeyId?: string | null;
  previousSigningSecretValidUntilUtc: string;
  newSigningSecret: string;
}

export interface EventHubWebhookDeliveryListItem {
  id: string;
  subscriptionId: string;
  outboxMessageId: string;
  eventId: string;
  eventType: string;
  attempt: number;
  signatureKeyId: string;
  statusCode?: number | null;
  isSuccess: boolean;
  responseSnippet: string;
  errorMessage?: string | null;
  deliveredAtUtc?: string | null;
}
